import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from './cn';
import { useReducedMotionSafe, slideUp } from '../motion';

export type ToastTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

/**
 * Optional call-to-action rendered inside the toast (e.g. "View cart"). Kept
 * router-agnostic: the caller supplies the click handler so this ui primitive
 * never imports the router. Clicking it also dismisses the toast.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Auto-dismiss delay in ms. Set 0 to require manual dismissal. */
  duration?: number;
  action?: ToastAction;
}

interface ToastItem extends Required<Pick<ToastOptions, 'title' | 'tone'>> {
  id: number;
  description?: string;
  duration: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneAccent: Record<ToastTone, string> = {
  neutral: 'border-l-fg-subtle',
  success: 'border-l-success',
  danger: 'border-l-danger',
  warning: 'border-l-warning',
  info: 'border-l-info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const animate = useReducedMotionSafe();

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = ++idRef.current;
      const item: ToastItem = {
        id,
        title: opts.title,
        description: opts.description,
        tone: opts.tone ?? 'neutral',
        duration: opts.duration ?? 5000,
        action: opts.action,
      };
      setToasts((list) => [...list, item]);
      if (item.duration > 0) {
        window.setTimeout(() => dismiss(id), item.duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed bottom-0 right-0 z-toast flex w-full max-w-sm flex-col gap-2 p-4"
          role="region"
          aria-label="Notifications"
        >
          <AnimatePresence initial={false}>
            {toasts.map((t) => (
              <motion.div
                key={t.id}
                layout={animate}
                variants={animate ? slideUp : undefined}
                initial={animate ? 'hidden' : false}
                animate="visible"
                exit={animate ? 'exit' : undefined}
                role={t.tone === 'danger' ? 'alert' : 'status'}
                aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
                className={cn(
                  'pointer-events-auto flex flex-col gap-2 rounded-lg border border-l-4 border-border bg-surface p-4 shadow-lg',
                  toneAccent[t.tone],
                )}
              >
                {/* Header row: title + dismiss. */}
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-fg">{t.title}</p>
                  <button
                    type="button"
                    onClick={() => dismiss(t.id)}
                    aria-label="Dismiss notification"
                    className="-mr-1 -mt-1 shrink-0 rounded-sm p-1 text-fg-subtle transition-colors hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                {/* Description: its own full-width row. */}
                {t.description && <p className="text-sm text-fg-muted">{t.description}</p>}
                {/* Action: its own full-width row. */}
                {t.action && (
                  <button
                    type="button"
                    onClick={() => {
                      t.action!.onClick();
                      dismiss(t.id);
                    }}
                    className="w-full rounded-md border border-border px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t.action.label}
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** Access the toast API: `const { toast } = useToast()`. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>.');
  return ctx;
}

const NOOP_TOAST: ToastContextValue = { toast: () => -1, dismiss: () => {} };

/**
 * Like {@link useToast} but degrades to a no-op when rendered outside a
 * <ToastProvider> instead of throwing. Use in pages whose notifications are a
 * nice-to-have (e.g. a "sample added" confirmation) so they stay renderable in
 * isolation (tests, storybook) without every harness wiring up the provider.
 */
export function useOptionalToast(): ToastContextValue {
  return useContext(ToastContext) ?? NOOP_TOAST;
}
